import React, { FC } from 'react';
import styles from '../journey-explorer.module.scss';
import { ChurnInsightsResult, FirstDays } from '../../../../models/journey-explorer/journey-explorer';
import { FIRST_DAYS_OPTIONS, formatCompactCurrency, joinWords } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import ChurnCohortChart from './churn-cohort-chart';
import ChurnSignalCard from './churn-signal-card';

const HIDDEN_SIGNALS =
	'Signals compare churned and active customers, so they are hidden while the Churn status filter is on. Remove it to see them.';

interface ChurnInsightsPanelProps {
	insights: ChurnInsightsResult | null;
	loading: boolean;
	lookup: Map<string, InteractionInfo>;
	churnEvents: string[];
	churnStatus: string | null;
	firstDays: FirstDays | null;
	anchor: string | null;
	currency: string;
	onFirstDaysChange: (firstDays: FirstDays | null) => void;
	onSelect: (name: string) => void;
	onClose: () => void;
}

const ChurnInsightsPanel: FC<ChurnInsightsPanelProps> = ({
	insights,
	loading,
	lookup,
	churnEvents,
	churnStatus,
	firstDays,
	anchor,
	currency,
	onFirstDaysChange,
	onSelect,
	onClose,
}) => {
	const liftScale = Math.max(1, ...(insights?.higher.map((signal) => signal.lift_high) ?? []));
	const timeBox = firstDays ? `in their first ${firstDays} days` : 'at any time';
	const excluded = joinWords(churnEvents.map((name) => resolveInfo(lookup, name).label));

	return (
		<div className={`${styles.panel_body} ${loading && insights ? styles.loading : ''}`}>
			<div className={styles.panel_header}>
				<span className={styles.panel_title}>Is it getting worse?</span>
				<button type="button" className={styles.panel_close} title="Close" aria-label="Close" onClick={onClose}>
					✕
				</button>
			</div>
			{!insights ? (
				<div className={styles.panel_subtitle}>{loading ? 'Loading churn insights…' : 'Churn insights are not available.'}</div>
			) : (
				<>
					<div className={styles.panel_subtitle}>Churn within {insights.follow_up_days} days of starting, by start month.</div>
					<ChurnCohortChart cohorts={insights.cohorts} followUpDays={insights.follow_up_days} />
					<div className={styles.insights_title}>What&apos;s linked to churn</div>
					{churnStatus ? (
						<div className={styles.panel_note}>{HIDDEN_SIGNALS}</div>
					) : (
						<>
							<div className={styles.panel_subtitle}>
								Customers who did each interaction {timeBox}, and how many churned within {insights.follow_up_days} days of
								it, against the {insights.base_churn_pct.toFixed(1)}% average.
							</div>
							<div className={`${styles.segmented} ${styles.insights_tabs}`}>
								{FIRST_DAYS_OPTIONS.map((option) => (
									<button
										key={option.label}
										type="button"
										aria-pressed={firstDays === option.key}
										className={`${styles.segment} ${firstDays === option.key ? styles.segment_active : ''}`}
										onClick={() => onFirstDaysChange(option.key)}
									>
										{option.label}
									</button>
								))}
							</div>
							<div className={`${styles.insights_heading} ${styles.churn_text}`}>Higher churn, ranked by value at risk</div>
							{insights.higher.length === 0 && <div className={styles.insights_empty}>No interaction stands out with higher churn.</div>}
							{insights.higher.map((signal) => (
								<ChurnSignalCard
									key={signal.name}
									signal={signal}
									lookup={lookup}
									tone="higher"
									minCustomers={insights.min_customers}
									liftScale={liftScale}
									currency={currency}
									selected={anchor === signal.name}
									onSelect={onSelect}
								/>
							))}
							<div className={`${styles.insights_heading} ${styles.green_text}`}>Lower churn</div>
							{insights.lower.length === 0 && <div className={styles.insights_empty}>No interaction stands out with lower churn.</div>}
							{insights.lower.map((signal) => (
								<ChurnSignalCard
									key={signal.name}
									signal={signal}
									lookup={lookup}
									tone="lower"
									minCustomers={insights.min_customers}
									liftScale={liftScale}
									currency={currency}
									selected={anchor === signal.name}
									onSelect={onSelect}
								/>
							))}
							<div className={styles.insights_footnote}>
								The time box stops long-standing customers looking safer simply because they stayed longer. Faded cards have
								too few customers, or a range that includes the average.
								{insights.avg_lifetime_value !== null &&
									` Value at risk = churn above the average × customers × ${formatCompactCurrency(
										insights.avg_lifetime_value,
										currency
									)} average lifetime value.`}
								{excluded &&
									` ${excluded} ${
										churnEvents.length === 1 ? 'is left out because it is' : 'are left out because they are'
									} part of churning.`}
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default ChurnInsightsPanel;
